import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { getErrorMessage } from "@/lib/api";
import Mapping from "./Mapping";
import CoaReview from "./CoaReview";

// TB / Mapping is the one door to every mapping the engagement keeps. Each kind stays in its own existing
// store with its own purpose, period and version — nothing is copied into a shared table:
//
//   coa      COA/TB account → FS line & audit area     accounts (fs_line, audit_area)     edited here
//   review   COA review & FS mapping basis             accounts (status_basis, notes)     edited here (moved from Perencanaan & Risiko)
//   sources  file column → dataset field; GL profiles  datasets.column_mapping, gl_mapping_profiles   edited in Data Intake (per upload)
//   areas    account → audit area / cycle              accounts.audit_area                read view; edit on "coa"
//   papers   workpaper → binder index                  master-binder mappings              decided in Binder Audit
//
// Views that edit elsewhere show the current state and link to the place that owns the decision.
export const VIEWS = [
  { key: "coa", label: "COA / TB → Pos LK & Area", purpose: "Tujuan: memetakan setiap akun COA/TB perikatan ke pos laporan keuangan dan area audit.", store: "Sumber: akun perikatan (koleksi accounts). Periode: periode perikatan. Versi: riwayat perubahan & alasan per akun." },
  { key: "review", label: "Reviu COA & Pemetaan LK", purpose: "Tujuan: reviu kelengkapan, dasar status dan catatan pemetaan akun sebelum perencanaan.", store: "Sumber: akun perikatan yang sama. Periode: periode perikatan. Versi: setiap perubahan tercatat di log audit." },
  { key: "sources", label: "Kolom Sumber Data", purpose: "Tujuan: memetakan kolom berkas klien ke field dataset (TB, GL, subledger) dan profil pemetaan GL yang dapat dipakai ulang.", store: "Sumber: dataset per unggahan dan profil pemetaan GL berversi per entitas. Diubah di Data Intake." },
  { key: "areas", label: "Area / Siklus", purpose: "Tujuan: melihat akun per area audit/siklus dan akun yang belum berarea.", store: "Sumber: area audit pada akun perikatan. Diubah pada subtab COA / TB." },
  { key: "papers", label: "Kertas Kerja", purpose: "Tujuan: melihat keputusan pemetaan kertas kerja ke indeks binder dan hubungan akun ke kertas kerja.", store: "Sumber: keputusan pemetaan binder (berversi, beralasan). Diputuskan di Binder Audit; kertas kerja per akun di Kertas Kerja Akun." },
];

function Link({ tab, extra, children, testid }) {
  const [, setSp] = useSearchParams();
  return <button type="button" className="underline text-sky-700" data-testid={testid} onClick={() => setSp({ tab, ...(extra || {}) })}>{children}</button>;
}

function SourceMappings({ id }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.all([api.get(`/engagements/${id}/datasets`), api.get(`/engagements/${id}/gl-mapping-profiles`).catch(() => ({ data: [] }))])
      .then(([d, p]) => { if (alive) setData({ datasets: d.data || [], profiles: p.data || [] }); })
      .catch((e) => { if (alive) setData({ error: getErrorMessage(e) }); });
    return () => { alive = false; };
  }, [id]);
  if (!data) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  if (data.error) return <p className="text-sm text-red-700">{data.error}</p>;
  return (
    <div className="space-y-3">
      <div className="section-card overflow-x-auto">
        <div className="px-4 py-2 border-b border-border font-head font-semibold text-sm">Dataset & pemetaan kolom</div>
        <table className="w-full audit-table text-xs" data-testid="mapping-sources-datasets">
          <thead><tr><th>Berkas</th><th>Jenis</th><th>Versi</th><th>Status</th><th>Kolom terpetakan</th><th>Diunggah</th></tr></thead>
          <tbody>
            {data.datasets.length === 0 && <tr><td colSpan={6} className="text-muted-foreground">Belum ada dataset.</td></tr>}
            {data.datasets.map((d) => (
              <tr key={d.id}>
                <td className="break-all">{d.source_filename}</td><td>{d.dataset_type}</td><td>{d.version ?? "—"}</td><td>{d.status}</td>
                <td>{Object.values(d.column_mapping || {}).filter(Boolean).length}</td><td>{(d.uploaded_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="section-card overflow-x-auto">
        <div className="px-4 py-2 border-b border-border font-head font-semibold text-sm">Profil pemetaan GL (dapat dipakai ulang)</div>
        <table className="w-full audit-table text-xs" data-testid="mapping-sources-profiles">
          <thead><tr><th>Nama</th><th>Entitas</th><th>Versi</th><th>Dasar</th></tr></thead>
          <tbody>
            {data.profiles.length === 0 && <tr><td colSpan={4} className="text-muted-foreground">Belum ada profil.</td></tr>}
            {data.profiles.map((p) => <tr key={p.id || p.profile_id}><td>{p.name}</td><td>{p.entity_id || "—"}</td><td>{p.version ?? "—"}</td><td>{p.basis || "—"}</td></tr>)}
          </tbody>
        </table>
      </div>
      <p className="text-xs">Pemetaan kolom ditetapkan per unggahan di <Link tab="dataintake" testid="mapping-open-dataintake">Data Intake</Link>.</p>
    </div>
  );
}

function AreaSummary({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/engagements/${id}/accounts`).then((r) => { if (alive) setRows(r.data.accounts || []); }).catch((e) => { if (alive) setRows({ error: getErrorMessage(e) }); });
    return () => { alive = false; };
  }, [id]);
  if (!rows) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  if (rows.error) return <p className="text-sm text-red-700">{rows.error}</p>;
  const byArea = {};
  rows.filter((a) => a.active !== false).forEach((a) => { const k = a.audit_area || ""; (byArea[k] = byArea[k] || []).push(a); });
  const areas = Object.keys(byArea).sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  return (
    <div className="space-y-3">
      <div className="section-card overflow-x-auto">
        <table className="w-full audit-table text-xs" data-testid="mapping-areas">
          <thead><tr><th>Area audit / siklus</th><th className="text-right">Akun aktif</th><th>Belum terpetakan ke pos LK</th></tr></thead>
          <tbody>
            {areas.length === 0 && <tr><td colSpan={3} className="text-muted-foreground">Belum ada akun.</td></tr>}
            {areas.map((k) => (
              <tr key={k || "none"} className={k ? "" : "bg-amber-50/60"} data-testid={`mapping-area-${k || "none"}`}>
                <td>{k || "Belum ada area"}</td><td className="text-right">{byArea[k].length}</td><td>{byArea[k].filter((a) => !a.fs_line).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs">Ubah area pada subtab COA / TB; prosedur per area ada di <Link tab="areas" testid="mapping-open-areas">Area Audit</Link>.</p>
    </div>
  );
}

function PaperMappings({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/engagements/${id}/master-binder/mappings`).then((r) => { if (alive) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (alive) setRows({ error: getErrorMessage(e) }); });
    return () => { alive = false; };
  }, [id]);
  if (!rows) return <p className="text-sm text-muted-foreground">Memuat…</p>;
  if (rows.error) return <p className="text-sm text-red-700">{rows.error}</p>;
  return (
    <div className="space-y-3">
      <div className="section-card overflow-x-auto">
        <table className="w-full audit-table text-xs" data-testid="mapping-papers">
          <thead><tr><th>Objek</th><th>Indeks kertas kerja</th><th>Alasan</th><th>Oleh</th><th>Waktu</th><th>Status</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-muted-foreground">Belum ada keputusan pemetaan kertas kerja.</td></tr>}
            {rows.slice(0, 200).map((m) => (
              <tr key={m.id} className={m.removed_at ? "opacity-60" : ""}>
                <td>{m.object_type} · {m.object_id}</td><td>{m.wp_ref}</td><td className="break-words">{m.note}</td>
                <td>{m.mapped_by}</td><td>{(m.mapped_at || "").slice(0, 16).replace("T", " ")}</td>
                <td>{m.removed_at ? `Dibatalkan: ${m.removed_reason || ""}` : "Berlaku"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs">Keputusan pemetaan diambil di <Link tab="binder" testid="mapping-open-binder">Binder Audit</Link>; kertas kerja per akun di <Link tab="accounts" testid="mapping-open-accounts">Kertas Kerja Akun</Link>.</p>
    </div>
  );
}

export default function MappingWorkspace({ id }) {
  const [sp, setSp] = useSearchParams();
  const view = VIEWS.some((v) => v.key === sp.get("view")) ? sp.get("view") : "coa";
  const current = VIEWS.find((v) => v.key === view);
  return (
    <div className="space-y-3" data-testid="mapping-workspace">
      <div role="tablist" aria-label="Jenis pemetaan" className="flex flex-wrap gap-1">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" role="tab" aria-selected={v.key === view} data-testid={`mapping-view-${v.key}`}
            onClick={() => setSp(v.key === "coa" ? { tab: "mapping" } : { tab: "mapping", view: v.key })}
            className={`min-h-9 px-3 text-xs border rounded-sm ${v.key === view ? "bg-slate-800 text-white border-slate-800" : "bg-white border-border hover:bg-slate-50"}`}>
            {v.label}
          </button>
        ))}
      </div>
      <div className="text-xs text-muted-foreground border-l-2 border-sky-300 pl-2" data-testid="mapping-view-purpose">
        <div>{current.purpose}</div><div>{current.store}</div>
      </div>
      {view === "coa" && <Mapping id={id} />}
      {view === "review" && <CoaReview id={id} />}
      {view === "sources" && <SourceMappings id={id} />}
      {view === "areas" && <AreaSummary id={id} />}
      {view === "papers" && <PaperMappings id={id} />}
    </div>
  );
}
